import React, { useState, useEffect } from 'react';
import './App.css';
import './pages/Home.css';
import './pages/Projects.css';

function Cursor() {
  const [pos, setPos] = useState({ x: -100, y: -100 });
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const move = e => setPos({ x: e.clientX, y: e.clientY });
    const hide = () => setHidden(true);
    const show = () => setHidden(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseleave', hide);
    window.addEventListener('mouseenter', show);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseleave', hide);
      window.removeEventListener('mouseenter', show);
    };
  }, []);
  return (
    <div
      className={`cursor ${hidden ? 'cursor--hidden' : ''}`}
      style={{ left: pos.x, top: pos.y }}
    />
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <a href="#top" className="nav-brand">Rishi Pabbathi</a>
      <div className="nav-links nav-links--center">
        <a href="#about" className="nav-link nav-link--pill">About</a>
        <a href="#experience" className="nav-link nav-link--pill">Experience</a>
        <a href="#projects" className="nav-link nav-link--pill">Projects</a>
      </div>
    </nav>
  );
}

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

const skills = [
  { cat: 'Languages', items: ['Python', 'Java', 'MySQL', 'HTML', 'CSS', 'JavaScript', 'LaTeX', 'Git'] },
  { cat: 'Frameworks', items: ['Spring Boot', 'Streamlit', 'JDBC', 'Maven', 'REST APIs'] },
  { cat: 'Tools', items: ['GitHub', 'VS Code', 'IntelliJ', 'Google Colab', 'Vercel'] },
  { cat: 'Management', items: ['Market Research', 'Data Analysis', 'CRM', 'Power BI', 'MS Office', 'Gephi'] },
];

const courses = [
  'Data Structures & Algorithms', 'Deep Neural Networks',
  'Natural Language Processing', 'Computer Vision',
  'Object-Oriented Programming', 'Database Management Systems',
];

const interests = [
  { title: 'Generative Models', desc: 'Diffusion processes, SDEs, image synthesis and restoration' },
  { title: 'Clinical NLP', desc: 'Entity-accurate summarization, hallucination reduction in medical AI' },
  { title: 'Network Science', desc: 'Misinformation detection, graph centrality, community structures' },
  { title: 'Software Engineering', desc: 'Full-stack systems, RESTful APIs, scalable backend architecture' },
];

const experience = [
  {
    role: 'Software Development Intern',
    company: 'Nisum Consulting',
    period: 'Jul – Aug 2025',
    points: [
      'Built a CRUD Employee Management System using Java, Maven, JDBC, and MySQL',
      'Integrated frontend with backend via RESTful APIs',
      'Collaborated with senior developers to debug and improve code quality',
      'Worked with Git, Maven, Spring Boot, and basic frontend development',
    ],
  },
  {
    role: 'Marketing Intern',
    company: 'Café Niloufer',
    period: 'Jun – Jul 2025',
    points: [
      'Got personally mentored by the marketing head of the company',
      'Gained hands-on experience in retail operations including inventory management, financial tracking, and product positioning',
      'Conducted market research and competitive analysis, identifying key sales strategies and areas of improvement',
      'Assisted in refining customer relationship management strategies, resulting in improved customer retention',
    ],
  },
  {
    role: 'Marketing & Management Intern',
    company: 'Insugo Insurance Brokers',
    period: 'Jan – Feb 2025',
    points: [
      'Built a Streamlit web app to extract key fields from insurance policy documents',
      'Shadowed department heads across Sales, HR, Finance, Marketing, and Administration',
      'Participated in client meetings and gained real-world deal negotiation experience',
    ],
  },
];

const projects = [
  {
    id: 1,
    title: 'SDE-Based Image Denoising',
    tagline: 'Single-step implicit correction for multiplicative noise',
    category: 'ML Research',
    year: '2024',
    description: 'Developed a grid-based image denoising model using Stochastic Differential Equations. Images corrupted with multiplicative noise are restored using a novel single-step implicit SDE correction — replacing the expensive iterative diffusion sampling process.',
    highlights: [
      { label: 'Benchmark', value: ' Outperformed DnCNN & ID-CNN on PSNR/SSIM' },
      { label: 'Innovation', value: ' Single-step correction vs. iterative sampling' },
      { label: 'Noise types', value: ' High-noise and mixed-noise conditions' },
    ],
    tech: ['Python', 'SDE Modeling', 'HuggingFace', 'Scipy', 'Google Colab'],
    paper: `${process.env.PUBLIC_URL}/ID_4_ASCML2026.pdf`,
  },
  {
    id: 2,
    title: 'Fake News Detection via Network Analysis',
    tagline: 'Mapping rumour spread patterns with graph theory',
    category: 'NLP / Graph',
    year: '2024',
    description: 'Built a fake news detection and analysis system on the PHEME dataset to study how rumours propagate on social media. Constructed interaction networks in Gephi and applied centrality metrics and modularity analysis to expose echo chambers and super-spreader nodes.',
    highlights: [
      { label: 'Dataset', value: ' PHEME — real social media rumour threads' },
      { label: 'Methods', value: ' Centrality metrics + modularity analysis' },
      { label: 'Finding', value: ' Structural polarization & weak cross-community links' },
    ],
    tech: ['Python', 'Gephi', 'Network Analysis', 'PHEME Dataset', 'Data Cleaning'],
    paper: `${process.env.PUBLIC_URL}/Project Report.pdf`,
  },
  {
    id: 3,
    title: 'Entity-Accurate Clinical Summarization',
    tagline: 'NER-integrated summarization to minimize hallucinations',
    category: 'Clinical AI',
    year: '2024',
    description: 'Designed a novel pipeline for generating concise, factually reliable summaries of patient notes from the MIMIC-III dataset. Named Entity Recognition is integrated into the summarization loop so that diseases, drugs, procedures, and patient details are always preserved and correctly attributed.',
    highlights: [
      { label: 'Dataset', value: ' MIMIC-III clinical notes' },
      { label: 'Architecture', value: ' BERT (NER) + T5 (summarization)' },
      { label: 'Goal', value: ' Zero hallucination of critical clinical entities' },
    ],
    tech: ['Python', 'PyTorch', 'BERT', 'T5', 'MIMIC-III', 'NER'],
    github: 'https://github.com/rishipabbathi',
  },
  {
    id: 4,
    title: 'Employee Management System',
    tagline: 'Skeleton structure implementing core SE concepts',
    category: 'Software Engineering',
    year: '2025',
    description: 'Built a Maven based Java 8 application to access and manipulate employee data using CRUD operations. This project uses a reusable DAO layer with prepared resource management system supporting the Insert, Update, Delete and Retrieve ops. JDBC API is used to connect the MySql database.',
    highlights: [
      { label: 'Functionality', value: ' Insert, Update, Delete, and Retrieve employee records' },
      { label: 'Architecture', value: ' Reusable DAO layer with clean separation of concerns' },
      { label: 'Database', value: ' MySQL integration using JDBC API' },
    ],
    tech: ['JAVA', 'SpringBoot', 'JDBC', 'Maven', 'MySql'],
    github: 'https://github.com/rishipabbathi',
  },
];

const cats = ['All', 'ML Research', 'NLP / Graph', 'Clinical AI', 'Software Engineering'];

export default function App() {
  useReveal();
  const [openExp, setOpenExp] = useState(null);
  const [active, setActive] = useState('All');
  const [openProject, setOpenProject] = useState(null);
  const filtered = active === 'All' ? projects : projects.filter(p => p.category === active);

  return (
    <div className="app" id="top">
      <Cursor />
      <Navbar />

      <div className="page home-page">

        {/* HERO */}
        <section className="hero">
          <p className="hero-eyebrow reveal">B.Tech Artificial Intelligence · Mahindra University · Hyderabad</p>
          <h1 className="hero-name reveal reveal-delay-1">Rishi Pabbathi</h1>
          <p className="hero-bio reveal reveal-delay-2">
            I try to research and build intelligent systems
          </p>
          <div className="hero-actions reveal reveal-delay-3">
            <a href="#projects" className="btn btn-solid">View Projects</a>
            <a href="https://github.com/rishipabbathi" target="_blank" rel="noreferrer" className="btn btn-outline">GitHub</a>
            <a href="https://linkedin.com/in/rishipabbathi" target="_blank" rel="noreferrer" className="btn btn-outline">LinkedIn</a>
            <a href={`${process.env.PUBLIC_URL}/Rishi_Pabbathi.pdf`} target="_blank" rel="noreferrer" className="btn btn-outline">Resume ↗</a>
          </div>
          <div className="hero-meta reveal reveal-delay-4">
            <span>rishivijayviswas@gmail.com</span>
            <span className="hero-meta-dot">·</span>
            <span>3 Research Projects</span>
            <span className="hero-meta-dot">·</span>
            <span>3 Internships</span>
          </div>
        </section>

        <div className="divider reveal" />

        {/* ABOUT */}
        <section id="about" className="section reveal">
          <span className="eyebrow">About</span>
          <div className="about-grid">
            <div className="about-main">
              <h2 className="section-heading">Building AI that works<br />in the real world.</h2>
              <p className="body-text">
                Third-year AI student at Mahindra University with a parallel interest in consulting and business strategy. I work at the intersection of intelligent systems and real world decision making.
              </p>
              <p className="body-text">
                Having worked across insurance, retail, and software, I've seen firsthand where AI can cut through inefficiency. I'm currently open to research and consulting opportunities at that intersection.
              </p>
            </div>
            <div className="about-side">
              <div className="edu-block">
                <p className="edu-label">Education</p>
                <p className="edu-school">Mahindra University</p>
                <p className="edu-deg">B.Tech — Artificial Intelligence</p>
                <p className="edu-period">2023 – Present</p>
              </div>
              <div className="courses-block">
                <p className="edu-label">Relevant coursework</p>
                <div className="courses-tags">
                  {courses.map(c => <span className="tag" key={c}>{c}</span>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="divider reveal" />

        {/* RESEARCH INTERESTS — original grid, with hover */}
        <section className="section">
          <span className="eyebrow reveal">Research Interests</span>
          <div className="interests-grid">
            {interests.map((item, i) => (
              <div className={`interest-item reveal reveal-delay-${i + 1}`} key={i}>
                <p className="interest-num">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="interest-title">{item.title}</h3>
                <p className="interest-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="divider reveal" />

        {/* SKILLS — original grid, with hover */}
        <section className="section">
          <span className="eyebrow reveal">Skills</span>
          <div className="skills-grid">
            {skills.map((g, i) => (
              <div className={`skill-group reveal reveal-delay-${i + 1}`} key={g.cat}>
                <p className="skill-cat">{g.cat}</p>
                <div className="skill-tags">
                  {g.items.map(s => <span className="tag" key={s}>{s}</span>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="divider reveal" />

        {/* EXPERIENCE — projects format, left-oriented */}
        <section id="experience" className="section">
          <span className="eyebrow reveal">Experience</span>
          <div className="proj-list">
            {experience.map((e, i) => (
              <div
                className="proj-row proj-row--slide"
                key={i}
                onClick={() => setOpenExp(openExp === i ? null : i)}
              >
                <div className="proj-row-header">
                  <div className="proj-row-left">
                    <span className="proj-num">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <h2 className="proj-name">{e.role}</h2>
                      <p className="proj-tagline">{e.company}</p>
                    </div>
                  </div>
                  <div className="proj-row-right">
                    <span className="tag">{e.period}</span>
                    <span className="proj-toggle">{openExp === i ? '−' : '+'}</span>
                  </div>
                </div>

                {openExp === i && (
                  <div className="proj-body" onClick={ev => ev.stopPropagation()}>
                    <ul className="exp-points">
                      {e.points.map((p, j) => <li key={j}>{p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="divider reveal" />

        {/* PROJECTS */}
        <section id="projects" className="section" style={{ padding: 0 }}>
          <span className="eyebrow reveal">Selected Projects</span>

          <div className="filter-row reveal reveal-delay-1">
            {cats.map(c => (
              <button key={c} onClick={() => setActive(c)}
                className={`filter-btn ${active === c ? 'filter-btn--active' : ''}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="proj-list">
            {filtered.map((p, i) => (
              <div
                className="proj-row proj-row--slide"
                key={p.id}
                onClick={() => setOpenProject(openProject === p.id ? null : p.id)}
              >
                <div className="proj-row-header">
                  <div className="proj-row-left">
                    <span className="proj-num">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <h2 className="proj-name">{p.title}</h2>
                      <p className="proj-tagline">{p.tagline}</p>
                    </div>
                  </div>
                  <div className="proj-row-right">
                    <span className="tag">{p.category}</span>
                    <span className="proj-toggle">{openProject === p.id ? '−' : '+'}</span>
                  </div>
                </div>

                {openProject === p.id && (
                  <div className="proj-body" onClick={e => e.stopPropagation()}>
                    <p className="proj-desc">{p.description}</p>
                    <div className="proj-highlights">
                      {p.highlights.map(h => (
                        <div className="hl-item" key={h.label}>
                          <span className="hl-label">{h.label}</span>
                          <span className="hl-val">{h.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="proj-footer">
                      <div className="proj-tech">
                        {p.tech.map(t => <span className="tag" key={t}>{t}</span>)}
                      </div>
                      <div className="proj-links">
                        {p.paper && (
                          <a href={p.paper} target="_blank" rel="noreferrer" className="proj-link">
                            View Paper ↗
                          </a>
                        )}
                        {p.github && (
                          <a href={p.github} target="_blank" rel="noreferrer" className="proj-link">
                            GitHub ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="proj-cta reveal">
            <div className="cta-left">
              <p className="cta-label">GET IN TOUCH</p>
              <a href="mailto:rishivijayviswas@gmail.com" className="cta-email">rishivijayviswas@gmail.com</a>
              <p className="cta-phone">+91 8143184369</p>
            </div>
            <div className="cta-right">
              <p className="cta-label">CONNECT</p>
              <div className="cta-links cta-links--icons">
                <a href="https://github.com/rishipabbathi" target="_blank" rel="noreferrer" className="cta-icon-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.268 2.75 1.026A9.578 9.578 0 0112 6.836a9.59 9.59 0 012.504.337c1.909-1.294 2.747-1.026 2.747-1.026.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"/></svg>
                  GitHub
                </a>
                <a href="https://linkedin.com/in/rishipabbathi" target="_blank" rel="noreferrer" className="cta-icon-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
                <a href="mailto:rishivijayviswas@gmail.com" className="cta-icon-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>
                  Email
                </a>
              </div>
            </div>
          </div>
        </section>

      </div>

      <footer className="footer">
        <div className="footer-inner">
          <span className="footer-left">© 2025 Rishi Vijay Viswas Pabbathi</span>
        </div>
      </footer>
    </div>
  );
}
